import {
  AnyAction,
  createSlice,
  PayloadAction,
  Reducer,
  Slice,
} from '@reduxjs/toolkit';

import { PaginationDefaults } from '../../../../shared/utils/pagination-defaults.util';
import { FiltersPayloadModel } from '../../../shared/models/filters-payload.model';
import { CampaignFilters } from '../../models/campaign-filters.model';
import { getCampaignsTotalCount } from '../../thunks/get-campaigns-total-count.thunk';
import { getCampaigns } from '../../thunks/get-campaigns.thunk';
import { CampaignsHeaderAndPaginationFilters } from './models/campaigns-header-and-pagination-filters.model';
import { CampaignsStateModel } from './models/campaigns-state.model';
import { CampaignsReducers } from './types/campaigns-reducers.type';

const filtersInitialState: CampaignsHeaderAndPaginationFilters = {
  header: {
    name: '',
    modelIds: [],
  },
  pagination: {
    totalCount: PaginationDefaults.totalCount,
    pageNumber: PaginationDefaults.pageNumber,
    size: PaginationDefaults.size,
    offset: PaginationDefaults.offset,
  },
};

const initialState: CampaignsStateModel = {
  campaigns: [],
  filters: filtersInitialState,
  areCampaignsFiltersVisible: false,
};

export const campaignsSlice: Slice<
  CampaignsStateModel,
  CampaignsReducers,
  'campaigns'
> = createSlice({
  name: 'campaigns',
  initialState,
  reducers: {
    filterCampaignsByName(
      state,
      { payload }: PayloadAction<FiltersPayloadModel<CampaignFilters>>
    ) {
      state.filters.header = {
        ...state.filters.header,
        name: payload.filterValue,
      };
    },

    filterCampaignsByBrand(
      state,
      { payload }: PayloadAction<number | undefined>
    ) {
      state.filters.header = {
        ...state.filters.header,
        brandId: payload,
      };
    },

    resetBrandFilter: state => {
      state.filters.header.modelIds = [];
    },

    filterCampaignsByModels(state, { payload }: PayloadAction<number[]>) {
      state.filters.header = {
        ...state.filters.header,
        modelIds: payload,
      };
    },

    filterCampaignsByStatus(
      state,
      { payload }: PayloadAction<number | undefined>
    ) {
      state.filters.header = {
        ...state.filters.header,
        status: payload,
      };
    },

    clearCampaignFilters(state) {
      state.filters = filtersInitialState;
    },

    addPagination(state, action) {
      state.filters.pagination.size = action.payload.size;
      state.filters.pagination.offset = action.payload.offset;
      state.filters.pagination.pageNumber = action.payload.pageNumber;
    },

    resetPagination(state, action) {
      state.filters.pagination.offset = PaginationDefaults.offset;
      state.filters.pagination.size = action.payload.size;
      state.filters.pagination.pageNumber = PaginationDefaults.pageNumber;
    },

    campaignsToggleFiltersVisibility(state) {
      state.areCampaignsFiltersVisible = !state.areCampaignsFiltersVisible;
    },

    clearCampaignsState(state) {
      Object.assign(state, initialState);
    },
  },

  extraReducers: builder => {
    builder
      .addCase(getCampaigns.fulfilled, (state, action) => {
        state.campaigns = action.payload;
      })
      .addCase(getCampaignsTotalCount.fulfilled, (state, action) => {
        state.filters.pagination.totalCount = action.payload;
      });
  },
});

export const {
  filterCampaignsByName,
  filterCampaignsByBrand,
  resetBrandFilter,
  filterCampaignsByModels,
  filterCampaignsByStatus,
  clearCampaignFilters,
  addPagination,
  resetPagination,
  campaignsToggleFiltersVisibility,
  clearCampaignsState,
} = campaignsSlice.actions;

const campaignsSliceReducers: Reducer<CampaignsStateModel, AnyAction> =
  campaignsSlice.reducer;

export default campaignsSliceReducers;
